import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Form, Button, Alert, Tabs, Tab, Spinner, Modal, Table, Badge } from "react-bootstrap";
import { FaSave, FaUndo, FaArrowLeft, FaPlus, FaCopy, FaEdit, FaArrowRight } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import HeadingBanner from "../components/HeadingBanner";
import LeftPanel from "../components/LeftPanel";
import {
  whoami,
  getCountries,
  getBannerScenarioDefinitions,
  createBannerScenarioDefinition,
  updateBannerScenarioDefinition,
  getBannersByTemplate,
  updateBannerContent,
  createBannerContent
} from "../services/APIClient";
import "../components/SchoolAdmin.css";

// ============================================
// HELPER COMPONENTS
// ============================================

const EditableField = ({ path, label, helpText, as = "input", rows = 1, value, onChange }) => (
  <Form.Group className="mb-3">
    <Form.Label className="fw-bold">{label}</Form.Label>
    <Form.Control
      as={as}
      rows={rows}
      value={value}
      onChange={(e) => onChange(path, e.target.value)}
    />
    {helpText && <Form.Text className="text-muted">{helpText}</Form.Text>}
  </Form.Group>
);

const InterpolatedPreview = ({ text, values = {} }) => {
  if (!text) return null;
  let preview = text;
  Object.entries(values).forEach(([key, value]) => {
    preview = preview.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), `<strong>${value}</strong>`);
  });
  return (
    <div className="mt-2 p-2 bg-light border rounded">
      <small className="text-muted">Preview: </small>
      <span dangerouslySetInnerHTML={{ __html: preview }} />
    </div>
  );
};

const PricingPreview = ({ text }) => {
  if (!text) return null;
  const htmlWithBreaks = text.replace(/\n/g, '<br />');
  return (
    <div className="mt-2 p-3 bg-light border rounded">
      <small className="text-muted d-block mb-2">Preview:</small>
      <span dangerouslySetInnerHTML={{ __html: htmlWithBreaks }} />
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

function VisualBannerEditor() {
  const navigate = useNavigate();
  const [teacherid, setTeacherid] = useState(101);
  const [name, setName] = useState("Admin User");

  // Reference data
  const [countries, setCountries] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  // Banner list for selected template
  const [banners, setBanners] = useState([]);
  const [loadingBanners, setLoadingBanners] = useState(false);

  // Selected banner for editing
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [content, setContent] = useState(null);
  const [originalContent, setOriginalContent] = useState(null);
  const [showDaysBefore, setShowDaysBefore] = useState('');
  const [originalShowDaysBefore, setOriginalShowDaysBefore] = useState('');
  const [definition, setDefinition] = useState(null);

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [hasChanges, setHasChanges] = useState(false);

  // Template modal state
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateModalMode, setTemplateModalMode] = useState('create'); // 'create' or 'edit'
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    scenario_type: '',
    display_name: '',
    description: '',
    clone_from: '',
  });

  // Create banner modal state
  const [showBannerModal, setShowBannerModal] = useState(false);
  const [creatingBanner, setCreatingBanner] = useState(false);
  const [bannerForm, setBannerForm] = useState({
    countryid: '',
    show_days_before: '',
  });

  // ============================================
  // DATA LOADING
  // ============================================

  useEffect(() => {
    Promise.all([
      whoami().then((r) => {
        setTeacherid(r.data.stakeholder.id);
        setName(r.data.stakeholder.name);
      }),
      getCountries().then(setCountries),
      loadTemplates(),
    ]).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (selectedTemplateId) {
      loadBanners();
    }
  }, [selectedTemplateId]);

  const loadTemplates = async () => {
    const response = await getBannerScenarioDefinitions();
    setTemplates(response.data);
    return response.data;
  };

  const loadBanners = async () => {
    setLoadingBanners(true);
    setSelectedBanner(null);
    setContent(null);
    try {
      const response = await getBannersByTemplate(selectedTemplateId);
      setBanners(response.data);
    } catch (error) {
      console.error("Failed to load banners:", error);
      setMessage({ type: 'danger', text: 'Failed to load banners' });
    } finally {
      setLoadingBanners(false);
    }
  };

  // ============================================
  // TEMPLATE HANDLERS
  // ============================================

  const handleTemplateChange = (e) => {
    if (hasChanges && !window.confirm('You have unsaved changes. Switch template anyway?')) return;
    setSelectedTemplateId(e.target.value);
  };

  const openCreateTemplate = () => {
    setTemplateForm({
      scenario_type: '',
      display_name: '',
      description: '',
      clone_from: templates.length > 0 ? templates[0].id : '',
    });
    setTemplateModalMode('create');
    setShowTemplateModal(true);
  };

  const openEditTemplate = () => {
    const tmpl = templates.find(t => t.id === selectedTemplateId);
    if (!tmpl) return;
    setTemplateForm({
      scenario_type: tmpl.scenario_type,
      display_name: tmpl.display_name,
      description: tmpl.description || '',
      clone_from: '',
    });
    setTemplateModalMode('edit');
    setShowTemplateModal(true);
  };

  const handleSaveTemplate = async () => {
    setSavingTemplate(true);
    try {
      if (templateModalMode === 'create') {
        const source = templates.find(d => d.id === templateForm.clone_from);
        const schema = source ? JSON.parse(JSON.stringify(source.schema)) : { fields: {} };

        await createBannerScenarioDefinition({
          scenario_type: templateForm.scenario_type,
          display_name: templateForm.display_name,
          description: templateForm.description,
          schema,
          is_active: true,
        });

        const updated = await loadTemplates();
        const created = updated.find(t => t.scenario_type === templateForm.scenario_type);
        if (created) setSelectedTemplateId(created.id);
        setMessage({ type: 'success', text: 'Template created!' });
      } else {
        await updateBannerScenarioDefinition(selectedTemplateId, {
          display_name: templateForm.display_name,
          description: templateForm.description,
        });
        await loadTemplates();
        setMessage({ type: 'success', text: 'Template updated!' });
      }

      setShowTemplateModal(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error("Failed to save template:", error);
      const errorMsg = error.response?.data?.error?.message || 'Failed to save template.';
      setMessage({ type: 'danger', text: errorMsg });
    } finally {
      setSavingTemplate(false);
    }
  };

  // ============================================
  // BANNER HANDLERS
  // ============================================

  const selectBanner = (banner) => {
    if (hasChanges && !window.confirm('You have unsaved changes. Switch banner anyway?')) return;

    const tmpl = templates.find(t => t.id === selectedTemplateId);
    setDefinition(tmpl);
    setSelectedBanner(banner);
    setContent(banner.content);
    setOriginalContent(JSON.parse(JSON.stringify(banner.content)));
    setShowDaysBefore(banner.show_days_before ?? '');
    setOriginalShowDaysBefore(banner.show_days_before ?? '');
    setHasChanges(false);
  };

  const openCreateBanner = () => {
    const hasGlobal = banners.some(b => !b.countryid);
    setBannerForm({ countryid: '', show_days_before: '', duplicateFromGlobal: hasGlobal });
    setShowBannerModal(true);
  };

  const handleCreateBanner = async () => {
    setCreatingBanner(true);
    try {
      let contentPayload;

      if (bannerForm.duplicateFromGlobal) {
        const globalBanner = banners.find(b => !b.countryid);
        contentPayload = globalBanner ? JSON.parse(JSON.stringify(globalBanner.content)) : {};
      } else {
        const tmpl = templates.find(t => t.id === selectedTemplateId);
        contentPayload = tmpl ? buildEmptyContentFromSchema(tmpl.schema) : {};
      }

      const payload = {
        scenario_definition_id: selectedTemplateId,
        locale: 'en-IE',
        content: contentPayload,
        is_active: true,
      };
      if (bannerForm.countryid) payload.countryid = bannerForm.countryid;
      if (bannerForm.show_days_before !== '') payload.show_days_before = Number(bannerForm.show_days_before);

      await createBannerContent(payload);
      setShowBannerModal(false);
      setMessage({ type: 'success', text: 'Banner created!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      await loadBanners();
    } catch (error) {
      console.error("Failed to create banner:", error);
      const errorMsg = error.response?.data?.error?.message || 'Failed to create banner.';
      setMessage({ type: 'danger', text: errorMsg });
    } finally {
      setCreatingBanner(false);
    }
  };

  // ============================================
  // CONTENT EDITING
  // ============================================

  const handleContentChange = (path, value) => {
    const newContent = JSON.parse(JSON.stringify(content));
    const keys = path.split('.');
    let current = newContent;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) current[keys[i]] = {};
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = value;
    setContent(newContent);
    setHasChanges(true);
  };

  const getValue = (path) => {
    if (!content) return '';
    const keys = path.split('.');
    let current = content;
    for (const key of keys) {
      if (current === undefined || current === null) return '';
      current = current[key];
    }
    return current || '';
  };

  const buildEmptyContentFromSchema = (schema) => {
    const result = {};
    const traverse = (obj, target) => {
      for (const key in obj) {
        if (obj[key].type && typeof obj[key].type === 'string') {
          target[key] = '';
        } else if (typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
          target[key] = {};
          traverse(obj[key], target[key]);
        }
      }
    };
    if (schema && schema.fields) traverse(schema.fields, result);
    return result;
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const payload = { content };
      if (showDaysBefore !== '') {
        payload.show_days_before = Number(showDaysBefore);
      }
      await updateBannerContent(selectedBanner.id, payload);
      setOriginalContent(JSON.parse(JSON.stringify(content)));
      setOriginalShowDaysBefore(showDaysBefore);
      setHasChanges(false);
      setMessage({ type: 'success', text: 'Saved!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      await loadBanners();
    } catch (error) {
      console.error("Failed to save:", error);
      setMessage({ type: 'danger', text: 'Failed to save changes.' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setContent(JSON.parse(JSON.stringify(originalContent)));
    setShowDaysBefore(originalShowDaysBefore);
    setHasChanges(false);
  };

  // ============================================
  // HELPERS
  // ============================================

  const getCountryName = (countryid) => {
    if (!countryid) return 'Global';
    const country = countries.find(c => c.id === countryid);
    return country ? country.name : countryid;
  };

  const templateHas = (feature) => {
    if (!definition || !definition.schema || !definition.schema.fields) return false;
    const fields = definition.schema.fields;
    switch (feature) {
      case 'extension': return !!fields.buttons?.extend;
      case 'messages': return !!fields.messages;
      case 'extendModal': return !!fields.modals?.extend;
      case 'purchaseModal': return !!fields.modals?.purchase;
      case 'expiredModal': return !!fields.modals?.expired;
      case 'extensionPrompt': return !!fields.banner?.extension_prompt;
      default: return false;
    }
  };

  const selectedTemplate = templates.find(t => t.id === selectedTemplateId);
  const selectedTemplateName = selectedTemplate?.display_name || '';
  const showDaysBeforeEnabled = selectedTemplate?.scenario_type !== 'trial_active';

  // ============================================
  // RENDER
  // ============================================

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status" />
        <p className="mt-3">Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <Container fluid className="class-admin-container">
        <HeadingBanner name={`Welcome ${name}!`} isLoggedIn={true} cloud={true} teacherid={teacherid} />

        <Row className="class-admin-row">
          <LeftPanel />
          <Col sm={{ span: 10, offset: 2 }} className="class-admin-content">

            {/* Header */}
            <div className="class-admin-header">
              <div className="d-flex align-items-center mb-3">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => navigate('/admin')}
                  className="me-3"
                >
                  <FaArrowLeft className="me-2" />Back
                </Button>
                <div>
                  <h2 className="class-admin-page-title mb-0">Banner Content Editor</h2>
                  <p className="class-admin-page-subtitle mb-0">Manage banner templates and their content</p>
                </div>
              </div>
            </div>

            {/* Template Selector */}
            <Card className="mb-3">
              <Card.Header className="bg-white">
                <h5 className="mb-0">Templates</h5>
              </Card.Header>
              <Card.Body>
                <div className="d-flex align-items-end gap-3 flex-wrap">
                  <Form.Group style={{ minWidth: '250px', maxWidth: '350px' }}>
                    <Form.Label className="fw-bold">Select Template:</Form.Label>
                    <Form.Select
                      value={selectedTemplateId || ''}
                      onChange={handleTemplateChange}
                    >
                      <option value="" disabled>-- Choose a template --</option>
                      {templates.map(t => (
                        <option key={t.id} value={t.id}>{t.display_name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Button variant="outline-success" onClick={openCreateTemplate}>
                    <FaPlus className="me-2" />New Template
                  </Button>
                  {selectedTemplateId && (
                    <Button variant="outline-secondary" onClick={openEditTemplate}>
                      <FaEdit className="me-2" />Edit Template
                    </Button>
                  )}
                </div>
                {selectedTemplateId && (
                  <Form.Text className="text-muted d-block mt-2">
                    {templates.find(t => t.id === selectedTemplateId)?.description}
                  </Form.Text>
                )}
              </Card.Body>
            </Card>

            {/* Alerts */}
            {message.text && (
              <Alert variant={message.type} dismissible onClose={() => setMessage({ type: '', text: '' })}>
                {message.text}
              </Alert>
            )}

            {/* Banner List */}
            {selectedTemplateId && (
              <Card className="mb-3">
                <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Banners <Badge bg="secondary" className="ms-2">{banners.length}</Badge></h5>
                  <Button variant="primary" size="sm" onClick={openCreateBanner}>
                    <FaPlus className="me-2" />Create Banner
                  </Button>
                </Card.Header>
                <Card.Body className="p-0">
                  {loadingBanners ? (
                    <div className="text-center p-4">
                      <Spinner animation="border" size="sm" className="me-2" />Loading banners...
                    </div>
                  ) : banners.length === 0 ? (
                    <div className="text-center p-4 text-muted">
                      No banners yet. Create one to get started.
                    </div>
                  ) : (
                    <Table hover className="mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Country</th>
                          {showDaysBeforeEnabled && <th>Show Days Before</th>}
                          <th>Active</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {banners.map(banner => (
                          <tr
                            key={banner.id}
                            className={selectedBanner?.id === banner.id ? 'table-active' : ''}
                            style={{ cursor: 'pointer' }}
                            onClick={() => selectBanner(banner)}
                          >
                            <td>{getCountryName(banner.countryid)}</td>
                            {showDaysBeforeEnabled && <td>{banner.show_days_before ?? '—'}</td>}
                            <td>
                              <Badge bg={banner.is_active ? 'success' : 'secondary'}>
                                {banner.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                            </td>
                            <td className="text-end">
                              <FaArrowRight className="text-muted" />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            )}

            {/* Banner Editor */}
            {selectedBanner && content && (
              <>
                <Card className="mb-3 border-primary">
                  <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                      Editing: {selectedTemplateName} — {getCountryName(selectedBanner.countryid)}
                    </h5>
                    <div>
                      <Button
                        variant="light"
                        size="sm"
                        onClick={handleSave}
                        disabled={saving || !hasChanges}
                        className="me-2"
                      >
                        <FaSave className="me-2" />{saving ? 'Saving...' : 'Save'}
                      </Button>
                      <Button
                        variant="outline-light"
                        size="sm"
                        onClick={handleReset}
                        disabled={!hasChanges}
                      >
                        <FaUndo className="me-2" />Reset
                      </Button>
                    </div>
                  </Card.Header>
                </Card>

                {hasChanges && (
                  <Alert variant="warning">
                    <strong>Unsaved changes!</strong> Click "Save" to apply your updates.
                  </Alert>
                )}

                <Tabs defaultActiveKey="banner" className="mb-3">

                  {/* BANNER TAB */}
                  <Tab eventKey="banner" title="Banner">
                    {showDaysBeforeEnabled && (
                      <Card className="mb-3">
                        <Card.Header><h6 className="mb-0">Display Settings</h6></Card.Header>
                        <Card.Body>
                          <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">Show Days Before <span className="text-muted fw-normal">(optional)</span></Form.Label>
                            <Form.Control
                              type="number"
                              min="0"
                              value={showDaysBefore}
                              onChange={(e) => { setShowDaysBefore(e.target.value); setHasChanges(true); }}
                              style={{ maxWidth: '200px' }}
                              placeholder="e.g. 30"
                            />
                            <Form.Text className="text-muted">Days before the target date to start showing this banner</Form.Text>
                          </Form.Group>
                        </Card.Body>
                      </Card>
                    )}

                    <Card className="mb-3">
                      <Card.Header><h6 className="mb-0">Banner Message</h6></Card.Header>
                      <Card.Body>
                        <EditableField
                          path="banner.message"
                          label="Banner Message"
                          helpText="Use {{days}} for days remaining, {{lastPurchaseYear}} for last purchase year"
                          as="textarea"
                          rows={2}
                          value={getValue('banner.message')}
                          onChange={handleContentChange}
                        />
                        <InterpolatedPreview
                          text={getValue('banner.message')}
                          values={{ days: '30', lastPurchaseYear: '2024/25' }}
                        />
                        {templateHas('extensionPrompt') && (
                          <>
                            <hr />
                            <EditableField
                              path="banner.extension_prompt"
                              label="Extension Prompt"
                              helpText="Shown after the main message when extension hasn't been applied"
                              value={getValue('banner.extension_prompt')}
                              onChange={handleContentChange}
                            />
                          </>
                        )}
                      </Card.Body>
                    </Card>
                  </Tab>

                  {/* BUTTONS TAB */}
                  <Tab eventKey="buttons" title="Buttons">
                    <Card className="mb-3">
                      <Card.Body>
                        {templateHas('extension') && (
                          <>
                            <h6 className="text-muted mb-3">Extend Trial Button</h6>
                            <Row>
                              <Col md={6}>
                                <EditableField
                                  path="buttons.extend.label"
                                  label="Extend Button Label"
                                  helpText="Default state"
                                  value={getValue('buttons.extend.label')}
                                  onChange={handleContentChange}
                                />
                              </Col>
                              <Col md={6}>
                                <EditableField
                                  path="buttons.extend.applied_label"
                                  label="Extension Applied Label"
                                  helpText="After extension is applied"
                                  value={getValue('buttons.extend.applied_label')}
                                  onChange={handleContentChange}
                                />
                              </Col>
                            </Row>
                            <hr />
                          </>
                        )}
                        <h6 className="text-muted mb-3">Purchase Button</h6>
                        <EditableField
                          path="buttons.purchase.label"
                          label="Purchase Button Label"
                          value={getValue('buttons.purchase.label')}
                          onChange={handleContentChange}
                        />
                      </Card.Body>
                    </Card>
                  </Tab>

                  {/* MODALS TAB */}
                  <Tab eventKey="modals" title="Pop-up Modals">
                    {templateHas('extendModal') && (
                      <Card className="mb-3">
                        <Card.Header className="bg-info text-white"><h6 className="mb-0">Extend Trial Modal</h6></Card.Header>
                        <Card.Body>
                          <EditableField path="modals.extend.title" label="Modal Title" value={getValue('modals.extend.title')} onChange={handleContentChange} />
                          <EditableField path="modals.extend.subtitle" label="Modal Subtitle" as="textarea" rows={2} value={getValue('modals.extend.subtitle')} onChange={handleContentChange} />
                          {templateHas('messages') && (
                            <>
                              <hr />
                              <h6 className="text-muted mb-3">Extension Messages</h6>
                              <Row>
                                <Col md={6}>
                                  <EditableField path="messages.extension_success" label="Success Message" helpText="Shown when extension is applied" value={getValue('messages.extension_success')} onChange={handleContentChange} />
                                </Col>
                                <Col md={6}>
                                  <EditableField path="messages.extension_error" label="Error Message" helpText="Shown when extension fails" value={getValue('messages.extension_error')} onChange={handleContentChange} />
                                </Col>
                              </Row>
                            </>
                          )}
                        </Card.Body>
                      </Card>
                    )}

                    {templateHas('purchaseModal') && (
                      <Card className="mb-3">
                        <Card.Header className="bg-info text-white"><h6 className="mb-0">Purchase Modal</h6></Card.Header>
                        <Card.Body>
                          <EditableField path="modals.purchase.title" label="Modal Title" helpText="Use {{days}} for days remaining" value={getValue('modals.purchase.title')} onChange={handleContentChange} />
                          <InterpolatedPreview text={getValue('modals.purchase.title')} values={{ days: '32' }} />
                          <EditableField path="modals.purchase.subtitle" label="Modal Subtitle" as="textarea" rows={3} value={getValue('modals.purchase.subtitle')} onChange={handleContentChange} />
                          <EditableField path="modals.purchase.pricing_message" label="Pricing Information" helpText="Use line breaks to format." as="textarea" rows={8} value={getValue('modals.purchase.pricing_message')} onChange={handleContentChange} />
                          <PricingPreview text={getValue('modals.purchase.pricing_message')} />
                        </Card.Body>
                      </Card>
                    )}

                    {templateHas('expiredModal') && (
                      <Card className="mb-3">
                        <Card.Header className="bg-danger text-white">
                          <h6 className="mb-0">{selectedTemplateName?.includes('Lapsed') ? 'Welcome Back Modal' : 'Expired Modal'}</h6>
                        </Card.Header>
                        <Card.Body>
                          <EditableField path="modals.expired.title" label="Modal Title" value={getValue('modals.expired.title')} onChange={handleContentChange} />
                          <EditableField path="modals.expired.subtitle" label="Modal Subtitle" helpText="Use {{lastPurchaseYear}} for the customer's last purchase year" as="textarea" rows={4} value={getValue('modals.expired.subtitle')} onChange={handleContentChange} />
                          <InterpolatedPreview text={getValue('modals.expired.subtitle')} values={{ lastPurchaseYear: '2024/25' }} />
                          <EditableField path="modals.expired.pricing_message" label="Pricing Information" helpText="Use line breaks to format." as="textarea" rows={8} value={getValue('modals.expired.pricing_message')} onChange={handleContentChange} />
                          <PricingPreview text={getValue('modals.expired.pricing_message')} />
                        </Card.Body>
                      </Card>
                    )}

                    {!templateHas('extendModal') && !templateHas('purchaseModal') && !templateHas('expiredModal') && (
                      <Alert variant="info">No modals configured for this template.</Alert>
                    )}
                  </Tab>

                  {/* URLS TAB */}
                  <Tab eventKey="urls" title="URLs & Contact">
                    <Card className="mb-3">
                      <Card.Body>
                        <EditableField path="urls.purchase_form" label="Purchase Form URL" helpText="Link to the purchase form" value={getValue('urls.purchase_form')} onChange={handleContentChange} />
                        <EditableField path="urls.support_email" label="Support Email Address" helpText="Email address for support contact" value={getValue('urls.support_email')} onChange={handleContentChange} />
                        <div className="mt-3 p-3 bg-light border rounded">
                          <small className="text-muted d-block mb-2">Contact Link Preview:</small>
                          <a href={`mailto:${getValue('urls.support_email')}`}>{getValue('urls.support_email')}</a>
                        </div>
                      </Card.Body>
                    </Card>
                  </Tab>
                </Tabs>

                {/* Bottom Save */}
                <div className="mt-4 mb-5">
                  <Button variant="primary" size="lg" onClick={handleSave} disabled={saving || !hasChanges} className="me-2">
                    <FaSave className="me-2" />{saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button variant="secondary" size="lg" onClick={handleReset} disabled={!hasChanges}>
                    <FaUndo className="me-2" />Reset Changes
                  </Button>
                </div>
              </>
            )}

          </Col>
        </Row>
      </Container>

      {/* Template Modal (Create / Edit) */}
      <Modal show={showTemplateModal} onHide={() => setShowTemplateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{templateModalMode === 'create' ? 'Create New Template' : 'Edit Template'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {templateModalMode === 'create' && (
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Template Type</Form.Label>
              <Form.Control
                type="text"
                value={templateForm.scenario_type}
                onChange={(e) => setTemplateForm({ ...templateForm, scenario_type: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                placeholder="e.g. trial_extended"
              />
              <Form.Text className="text-muted">Unique identifier (lowercase, underscores)</Form.Text>
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">Display Name</Form.Label>
            <Form.Control
              type="text"
              value={templateForm.display_name}
              onChange={(e) => setTemplateForm({ ...templateForm, display_name: e.target.value })}
              placeholder="e.g. Extended Trial"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={templateForm.description}
              onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
              placeholder="Describe when this scenario applies"
            />
          </Form.Group>

          {templateModalMode === 'create' && (
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Clone Structure From</Form.Label>
              <Form.Select
                value={templateForm.clone_from}
                onChange={(e) => setTemplateForm({ ...templateForm, clone_from: e.target.value })}
              >
                <option value="">-- Empty (start from scratch) --</option>
                {templates.map(t => (
                  <option key={t.id} value={t.id}>{t.display_name}</option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">Copy field structure from an existing template</Form.Text>
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTemplateModal(false)}>Cancel</Button>
          <Button
            variant={templateModalMode === 'create' ? 'success' : 'primary'}
            onClick={handleSaveTemplate}
            disabled={savingTemplate || !templateForm.display_name || (templateModalMode === 'create' && !templateForm.scenario_type)}
          >
            {savingTemplate ? <><Spinner animation="border" size="sm" className="me-2" />Saving...</> : (
              templateModalMode === 'create' ? <><FaPlus className="me-2" />Create</> : <><FaSave className="me-2" />Save</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Create Banner Modal */}
      <Modal show={showBannerModal} onHide={() => setShowBannerModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create Banner for {selectedTemplateName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">Country</Form.Label>
            <Form.Select
              value={bannerForm.countryid}
              onChange={(e) => setBannerForm({ ...bannerForm, countryid: e.target.value })}
            >
              <option value="">Global (All Countries)</option>
              {countries.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </Form.Select>
          </Form.Group>

          {showDaysBeforeEnabled && (
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Show Days Before <span className="text-muted fw-normal">(optional)</span></Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={bannerForm.show_days_before}
                onChange={(e) => setBannerForm({ ...bannerForm, show_days_before: e.target.value })}
                placeholder="e.g. 30"
              />
              <Form.Text className="text-muted">Days before the target date to start showing this banner</Form.Text>
            </Form.Group>
          )}

          {banners.some(b => !b.countryid) && (
            <Form.Check
              type="checkbox"
              id="duplicate-global"
              label="Duplicate content from Global banner as starting point"
              checked={bannerForm.duplicateFromGlobal}
              onChange={(e) => setBannerForm({ ...bannerForm, duplicateFromGlobal: e.target.checked })}
              className="mt-3"
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBannerModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleCreateBanner} disabled={creatingBanner}>
            {creatingBanner ? <><Spinner animation="border" size="sm" className="me-2" />Creating...</> : <><FaPlus className="me-2" />Create Banner</>}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default VisualBannerEditor;
